import { buildDemoItems, DemoRow } from './demoSeed';
import { buildPantrySnapshot, PantryItemView } from '../../redux/pantrySelectors';
import type { InventoryItem } from '../../redux/inventorySlice';
import { PantryLocation } from '../../utils/pantryLocation';

/**
 * The four cards under "what expires first" on the pantry empty state.
 *
 * This data is deliberately built here rather than passed in. It used to be a
 * `previewItems` prop that `PantryMobile` filled from the live pantry snapshot
 * — but `EmptyState` only mounts when `totalCount === 0`, so that snapshot was
 * empty every single time the preview was on screen, and the section rendered
 * four blank cards for every user (#624). Desktop passed nothing at all.
 *
 * A preview of a full pantry is by definition not the user's pantry, so the
 * component owns it and no caller can starve it again.
 */

/** Ties inside a freshness bucket are broken by soonest expiry. */
function byDays(a: PantryItemView, b: PantryItemView): number {
  // Different buckets: leave the snapshot's STATE_ORDER ranking alone.
  if (a.state !== b.state) return 0;
  if (a.days == null && b.days == null) return 0;
  if (a.days == null) return 1;
  if (b.days == null) return -1;
  return a.days - b.days;
}

/**
 * Builds the preview, sorted so the soonest-to-expire item really is on top.
 *
 * `buildPantrySnapshot` sorts by freshness bucket only, so ties keep insertion
 * order — which put "Poitrines de poulet" (2 days) above "Filets de saumon"
 * (1 day) and made the section label a lie. The `byDays` pass fixes that here,
 * NOT in `buildPantrySnapshot`, which feeds the real pantry screen.
 *
 * Nothing here touches Redux or Firestore; the items are synthetic and never
 * persisted, which is why localizing their names is safe (see demoSeed).
 *
 * @param language i18next language tag; anything starting with `en` gets English
 * @param today Injected for deterministic tests
 */
export function buildPreviewItems(
  language: string,
  today: Date = new Date(),
): Record<PantryLocation, PantryItemView[]> {
  const useEnglish = language.toLowerCase().startsWith('en');
  const pickName = (row: DemoRow) => (useEnglish ? row.nameEn : row.name);

  const items: InventoryItem[] = buildDemoItems(today, pickName).map((payload, index) => ({
    id: `preview-${index}`,
    name: payload.name,
    category: payload.category,
    dateAdded: payload.dateAdded ?? today.toISOString(),
    expiryDate: payload.expiry_date,
    location: payload.location,
    quantity: payload.quantity,
    source: 'demo',
  }));

  const snapshot = buildPantrySnapshot(items, today);

  const byLocation = {} as Record<PantryLocation, PantryItemView[]>;
  (Object.keys(snapshot.byLocation) as PantryLocation[]).forEach((location) => {
    byLocation[location] = [...snapshot.byLocation[location]].sort(byDays);
  });
  return byLocation;
}
